/**
 * Plan 9a: Revenue-target derivation.
 *
 * The per-member override (`profile.revenueTarget`) always wins. When unset,
 * derive from org-level multiplier × fully-loaded monthly cost — but only when
 * the caller passes a non-null cost. Pure: no I/O, no compensation fetching.
 */
export interface EffectiveRevenueTargetInput {
  memberRevenueTarget: number | null | undefined
  revenueTargetMultiplier: number | null | undefined
  fullyLoadedMonthlyCost: number | null | undefined
}

export function effectiveRevenueTarget(
  input: EffectiveRevenueTargetInput,
): number | null {
  const {
    memberRevenueTarget,
    revenueTargetMultiplier,
    fullyLoadedMonthlyCost,
  } = input

  if (memberRevenueTarget != null && memberRevenueTarget > 0) {
    return Number(memberRevenueTarget)
  }
  if (
    revenueTargetMultiplier != null &&
    revenueTargetMultiplier > 0 &&
    fullyLoadedMonthlyCost != null &&
    fullyLoadedMonthlyCost > 0
  ) {
    return Number(fullyLoadedMonthlyCost) * Number(revenueTargetMultiplier)
  }
  return null
}

/**
 * Compute fully-loaded monthly cost from a member compensation record.
 * Employees: (annualSalary + annualTaxes + annualBenefits) / 12.
 * Contractors: hourlyRate × weeklyCapacity × 4.33.
 *
 * Callers that do not pre-resolve taxes + benefits can pass zero and fall back
 * to the base salary × hoursPerWeek × weeksPerYear annualisation already
 * stored on `MemberCompensation`.
 */
export interface FullyLoadedCostInput {
  compensationType: 'HOURLY' | 'SALARY' | 'DAILY' | 'WEEKLY' | 'MONTHLY' | 'ANNUAL' | string
  baseAmount: number
  hoursPerWeek?: number
  weeksPerYear?: number
  annualTaxes?: number
  annualBenefits?: number
}

export function fullyLoadedMonthlyCost(
  input: FullyLoadedCostInput,
): number | null {
  const {
    compensationType,
    baseAmount,
    hoursPerWeek = 40,
    weeksPerYear = 52,
    annualTaxes = 0,
    annualBenefits = 0,
  } = input

  if (!baseAmount || baseAmount <= 0) return null

  let annual = 0
  switch (compensationType) {
    case 'HOURLY':
      annual = baseAmount * hoursPerWeek * 4.33 * 12
      break
    case 'DAILY':
      annual = baseAmount * 5 * weeksPerYear
      break
    case 'WEEKLY':
      annual = baseAmount * weeksPerYear
      break
    case 'MONTHLY':
      annual = baseAmount * 12
      break
    case 'SALARY':
    case 'ANNUAL':
    default:
      annual = baseAmount
      break
  }

  return (annual + annualTaxes + annualBenefits) / 12
}
